import _ from "lodash"

import Model from "../../model"
import { ID, WebSource, WebSourceFields, WebSourceFieldName } from "../../types"
import Command, { CommandName } from "../command"

export type SetWebSourceFieldsCommandState = {
  id: ID
  fields: WebSourceFields
  captured?: {
    oldSource: WebSource
  }
}

export default class SetWebSourceFieldsCommand extends Command {
  name: CommandName = "set-web-source-fields"
  state: SetWebSourceFieldsCommandState

  constructor(state: SetWebSourceFieldsCommandState) {
    super()
    this.state = state
  }

  // Possible conflicts:
  //   - source might have been deleted (Cancel)
  //   - goal might have been deleted (Resolve, Cancel)
  resolveConflicts(model: Model): SetWebSourceFieldsCommandState | null {
    const source = model.getSource(this.state.id)
    if (!source) return null

    const newState = { ..._.cloneDeep(this.state) }
    if (this.state.fields.goalId && !model.getGoal(this.state.fields.goalId))
      delete newState.fields.goalId

    if (Object.keys(newState.fields).length === 0) return null
    return newState
  }

  local(model: Model): any {
    const oldSource = model.getSource(this.state.id)
    if (!oldSource) return null

    this.state.captured = { oldSource }

    const userProvidedName = this.state.fields.hasOwnProperty(
      "userProvidedName"
    )
      ? this.state.fields.userProvidedName
      : oldSource.userProvidedName

    const scannedName = this.state.fields.hasOwnProperty("scannedName")
      ? this.state.fields.scannedName
      : oldSource.scannedName

    if (
      this.state.fields.hasOwnProperty("userProvidedName") ||
      this.state.fields.hasOwnProperty("scannedName") ||
      this.state.fields.hasOwnProperty("name")
    ) {
      model.setSourceField(
        this.state.id,
        "name",
        userProvidedName || scannedName
      )
    }

    for (const [field, value] of Object.entries(this.state.fields)) {
      if (
        field === "userProvidedName" ||
        field === "scannedName" ||
        field === "name"
      )
        continue
      model.setSourceField(this.state.id, field as WebSourceFieldName, value)
      if (
        field === "status" &&
        this.state.captured.oldSource.status == "new" &&
        value == "read"
      ) {
        model.setSourceField(this.state.id, "lastReadAt", new Date())
      }
    }
  }

  // TODO: this should only change fields that need to, not set every field again
  undo(): Command[] {
    if (!this.state.captured) throw new Error("No captured undo state")
    return [
      new SetWebSourceFieldsCommand({
        id: this.state.id,
        fields: sourceToFields(this.state.captured.oldSource),
      }),
    ]
  }
}

const sourceToFields = (source: WebSource): WebSourceFields => {
  const fields: WebSourceFields = {}
  if (source.type) fields.type = source.type
  if (source.author) fields.author = source.author
  if (source.name) fields.name = source.name
  fields.userProvidedName = source.userProvidedName
  if (source.publicationDate) fields.publicationDate = source.publicationDate
  if (source.scannedAt) fields.scannedAt = source.scannedAt
  fields.metadataError = source.metadataError
  return fields
}
